import { useCallback, useState } from 'react';
import { RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { refreshDashboard } from '../services/dashboardRefresh';

/**
 * RefreshDashboardButton — botón manual de refresco del dashboard.
 *
 * Ubicación en el header: entre el botón del Genio (NotebookPen) y el
 * botón de conexión (GitHubHeaderButton / LyonHeaderButton).
 *
 * Al hacer click:
 *  - dispara `refreshDashboard()` (ver `/services/dashboardRefresh.js`)
 *  - re-solicita: /user/me, /history/list, /workspace/external,
 *    /workspace/internal, /pro/usage, /user/settings
 *  - emite eventos globales para que los contextos actualicen su estado
 *  - NO recarga la página ni afecta el editor, la conversación o la
 *    generación IA en curso.
 *
 * Estado visual: el ícono gira mientras dura el refresco (animate-spin).
 */
const RefreshDashboardButton = () => {
  const { language } = useLanguage();
  const es = language === 'es';
  const [busy, setBusy] = useState(false);

  const onClick = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      await refreshDashboard();
      toast.success(es ? 'Dashboard actualizado' : 'Dashboard refreshed');
    } catch {
      toast.error(es ? 'No se pudo refrescar' : 'Refresh failed');
    } finally {
      setBusy(false);
    }
  }, [busy, es]);

  const title = es ? 'Refrescar Dashboard' : 'Refresh Dashboard';

  return (
    <button
      onClick={onClick}
      disabled={busy}
      title={title}
      aria-label={title}
      data-testid="refresh-dashboard-btn"
      className="relative p-1.5 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-wait"
    >
      <RotateCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
    </button>
  );
};

export default RefreshDashboardButton;
